import type { DetailFact, DetailType, EntityDetail } from "#shared/schemas/entityDetail.ts";
import { presentFacts } from "./facts.ts";

/** Plain-language label overrides for the Overview hero strip. */
const LABEL_MAP: Record<string, string> = {
  Released: "Year",
  "Tracks on release": "Tracks",
  "Tracks in library": "In library",
  "Alias (Legal name)": "Born as",
  "Legal name": "Born as",
  "Life-span": "Years active",
  "Begin date": "Years active",
};

/** Prefer these fact labels first, per entity type (max ~8). */
const HERO_PRIORITY: Partial<Record<DetailType, readonly string[]>> = {
  track: ["Year", "Released", "Album", "Label", "Length", "Artist"],
  album: ["Year", "Released", "Label", "Artist", "Tracks", "Tracks on release", "In library"],
  artist: [
    "Country",
    "Years active",
    "Born as",
    "In library",
    "Tracks in library",
    "Followers",
  ],
  playlist: ["Tracks", "Owner", "In library"],
  local_playlist: ["Tracks", "Folder"],
  genre: ["Slug", "Tracks"],
  folder: ["Playlists"],
};

function remapLabel(label: string): string {
  return LABEL_MAP[label] ?? label;
}

/**
 * Cap Overview hero facts at 6–8 lines with entity-appropriate priority
 * and plain-language labels. Everything else stays under More.
 */
export function pickHeroFacts(detail: EntityDetail): {
  hero: DetailFact[];
  rest: DetailFact[];
} {
  const present = presentFacts(detail.facts).map((f) => ({
    ...f,
    label: remapLabel(f.label),
  }));
  const priority = HERO_PRIORITY[detail.entity_type] ?? [];
  const max = 8;

  const hero: DetailFact[] = [];
  const used = new Set<string>();

  for (const want of priority) {
    if (hero.length >= max) break;
    const hit = present.find((f) => f.label === want || remapLabel(f.label) === want);
    if (!hit || used.has(hit.label)) continue;
    hero.push(hit);
    used.add(hit.label);
  }

  for (const fact of present) {
    if (hero.length >= max) break;
    if (used.has(fact.label)) continue;
    // Skip noisy technical rows from the hero.
    if (fact.label === "URI" || fact.label === "ISRC" || fact.label === "Enriched") continue;
    if (fact.label === "Disc number" || fact.label === "Date precision") continue;
    if (fact.label === "On Spotify" || fact.label === "Last created on Spotify") continue;
    hero.push(fact);
    used.add(fact.label);
  }

  const rest = present.filter((f) => !used.has(f.label));
  // Also include original facts that were remapped away from the present list labels
  // (rest already uses remapped present).
  return { hero, rest };
}
